import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    getAccountsPage,
    importAccounts as importAccountsApi,
    setCompleteOnVerdict as setCompleteOnVerdictApi,
    setDispatch as setDispatchApi,
    setSync as setSyncApi,
} from "../features/managedServices/services/managedServiceApi";
import type { ImportResult, MsAccount, MsAccountsPage } from "../features/managedServices/types";
import { redirectToLoginPage } from "../shared/sessionUtility";
import { AppDispatch, RootState } from "./types";

export interface MsAccountsState {
    isLoading: boolean;
    error: string | null;
    accounts: MsAccount[];
    total: number;
    page: number;
    pageSize: number;
    search: string;
    sortColumn: [string, boolean];
    importing: boolean;
    importResult: ImportResult | null;
}

const DEFAULT_PAGE_SIZE = 20;

const initialState: MsAccountsState = {
    isLoading: false,
    error: null,
    accounts: [],
    total: 0,
    page: 1,
    // Fallback only — callers seed from session.rowsPerPage on first load.
    pageSize: DEFAULT_PAGE_SIZE,
    search: "",
    sortColumn: ["avataxAccountId", true],
    importing: false,
    importResult: null,
};

interface AccountPatch {
    avataxAccountId: string;
    dispatchEnabled?: boolean;
    syncEnabled?: boolean;
    completeOnVerdict?: boolean;
}

const msAccountsSlice = createSlice({
    name: "managedServiceAccounts",
    initialState,
    reducers: {
        setLoading: (state: MsAccountsState, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setError: (state: MsAccountsState, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setAccountsPage: (state: MsAccountsState, action: PayloadAction<MsAccountsPage>) => {
            state.accounts = action.payload.items;
            state.total = action.payload.total;
            state.page = action.payload.page;
            state.pageSize = action.payload.pageSize;
        },
        setAccountsQuery: (
            state: MsAccountsState,
            action: PayloadAction<{
                page: number;
                pageSize: number;
                search: string;
                sortColumn: [string, boolean];
            }>
        ) => {
            state.page = action.payload.page;
            state.pageSize = action.payload.pageSize;
            state.search = action.payload.search;
            state.sortColumn = action.payload.sortColumn;
        },
        patchAccount: (state: MsAccountsState, action: PayloadAction<AccountPatch>) => {
            const target = state.accounts.find(
                account => account.avataxAccountId === action.payload.avataxAccountId
            );
            if (target) {
                if (action.payload.dispatchEnabled !== undefined) {
                    target.dispatchEnabled = action.payload.dispatchEnabled;
                }
                if (action.payload.syncEnabled !== undefined) {
                    target.syncEnabled = action.payload.syncEnabled;
                }
                if (action.payload.completeOnVerdict !== undefined) {
                    target.completeOnVerdict = action.payload.completeOnVerdict;
                }
            }
        },
        setImporting: (state: MsAccountsState, action: PayloadAction<boolean>) => {
            state.importing = action.payload;
        },
        setImportResult: (state: MsAccountsState, action: PayloadAction<ImportResult | null>) => {
            state.importResult = action.payload;
        },
    },
});

export const {
    setLoading,
    setError,
    setAccountsPage,
    setAccountsQuery,
    patchAccount,
    setImporting,
    setImportResult,
} = msAccountsSlice.actions;

export const selectMsAccounts = (state: RootState): MsAccountsState => state.managedServiceAccounts;

/**
 * Route a failed Managed-Service call. A dead session (401 — e.g. it expired or the API was
 * restarted) re-authenticates like the rest of the app instead of leaving the tab stuck on a stale
 * cookie; a missing internal/TSA role (403) gets an accurate access message; anything else falls
 * back to the caller's message. Returns true when it redirected (caller should not keep working).
 */
const reportApiError = (dispatch: AppDispatch, error: unknown, fallback: string): boolean => {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 401) {
        redirectToLoginPage();
        return true;
    }
    dispatch(
        setError(
            status === 403
                ? "You do not have access to Managed Service (an internal/TSA role is required)."
                : fallback
        )
    );
    return false;
};

export const loadAccounts =
    (
        page = 1,
        pageSize = DEFAULT_PAGE_SIZE,
        search = "",
        sortColumn: [string, boolean] = ["avataxAccountId", true]
    ) =>
    async (dispatch: AppDispatch): Promise<void> => {
        dispatch(setLoading(true));
        dispatch(setError(null));
        dispatch(setAccountsQuery({ page, pageSize, search, sortColumn }));
        try {
            dispatch(setAccountsPage(await getAccountsPage(page, pageSize, search, sortColumn)));
        } catch (error) {
            reportApiError(dispatch, error, "Unable to load enrolled accounts. Please try again.");
            // eslint-disable-next-line no-console
            console.error("loadAccounts failed:", error);
        } finally {
            dispatch(setLoading(false));
        }
    };

const reloadAccounts = (dispatch: AppDispatch, getState: () => RootState): void => {
    const { page, pageSize, search, sortColumn } = selectMsAccounts(getState());
    dispatch(loadAccounts(page, pageSize, search, sortColumn));
};

export const toggleDispatch =
    (avataxAccountId: string, enabled: boolean) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
        try {
            const result = await setDispatchApi(avataxAccountId, enabled);
            dispatch(patchAccount({ avataxAccountId, dispatchEnabled: result.dispatchEnabled }));
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("toggleDispatch failed:", error);
            if (!reportApiError(dispatch, error, "Could not update the dispatch flag.")) {
                reloadAccounts(dispatch, getState);
            }
        }
    };

export const toggleSync =
    (avataxAccountId: string, enabled: boolean) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
        try {
            const result = await setSyncApi(avataxAccountId, enabled);
            dispatch(patchAccount({ avataxAccountId, syncEnabled: result.syncEnabled }));
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("toggleSync failed:", error);
            if (!reportApiError(dispatch, error, "Could not update the send-back flag.")) {
                reloadAccounts(dispatch, getState);
            }
        }
    };

export const toggleCompleteOnVerdict =
    (avataxAccountId: string, enabled: boolean) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
        try {
            const result = await setCompleteOnVerdictApi(avataxAccountId, enabled);
            dispatch(
                patchAccount({
                    avataxAccountId,
                    completeOnVerdict: result.completeOnVerdict,
                })
            );
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("toggleCompleteOnVerdict failed:", error);
            if (
                !reportApiError(dispatch, error, "Could not update the complete-on-verdict flag.")
            ) {
                reloadAccounts(dispatch, getState);
            }
        }
    };

export const importCsv =
    (file: File) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
        dispatch(setImporting(true));
        dispatch(setError(null));
        dispatch(setImportResult(null));
        try {
            dispatch(setImportResult(await importAccountsApi(file)));
            reloadAccounts(dispatch, getState);
        } catch (error) {
            reportApiError(
                dispatch,
                error,
                "Import failed. Check that the file is a CSV with an account id in the first column, then try again."
            );
            // eslint-disable-next-line no-console
            console.error("importCsv failed:", error);
        } finally {
            dispatch(setImporting(false));
        }
    };

export default msAccountsSlice.reducer;
